import { useState } from "react";
import { Check, Copy, Mail, MessageCircle, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { writeTextToClipboard } from "@/utils/clipboard";
import { useToast } from "@/hooks/use-toast";
import { EmptyValue } from "./EmptyValue";

export function CopyButton({ value, label = "Copiar", className }: { value: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn("inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground", className)}
      onClick={async (event) => {
        event.stopPropagation();
        try {
          await writeTextToClipboard(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast({ title: "Não foi possível copiar", description: value });
        }
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-tone-success-fg" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export function ContactEmail({ email, className }: { email: string | null | undefined; className?: string }) {
  if (!email) return <EmptyValue label="Sem e-mail" />;
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-sm", className)}>
      <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <a href={`mailto:${email}`} className="truncate hover:underline">{email}</a>
      <CopyButton value={email} label="Copiar e-mail" />
    </span>
  );
}

/** Só dígitos com DDI 55 quando vier sem (padrão do wa.me). */
export function toWhatsAppNumber(phone: string): string | null {
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 10) return null;
  return digits.startsWith("55") && digits.length >= 12 ? digits : `55${digits}`;
}

export function formatPhoneBR(phone: string): string {
  const digits = phone.replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return phone;
}

export function ContactPhone({ phone, whatsapp = true, className }: { phone: string | null | undefined; whatsapp?: boolean; className?: string }) {
  if (!phone) return <EmptyValue label="Sem telefone" />;
  const waNumber = whatsapp ? toWhatsAppNumber(phone) : null;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm", className)}>
      <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="tabular-nums">{formatPhoneBR(phone)}</span>
      {waNumber && (
        <a
          href={`https://wa.me/${waNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir no WhatsApp"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-tone-success-fg hover:bg-tone-success-bg"
          onClick={(event) => event.stopPropagation()}
        >
          <MessageCircle className="h-3.5 w-3.5" />
        </a>
      )}
      <CopyButton value={phone} label="Copiar telefone" />
    </span>
  );
}
